
import {
  CurrencyPoundIcon,
  CalculatorIcon,
  TruckIcon,
  CubeIcon,
  ScaleIcon,
} from "@heroicons/react/24/outline";
import { useState } from "react";
import { ILead } from "../../../../models/Lead";
import ContactModals from "./ContactModals";
import PricingHeader from "./PricingHeader";
import QuotedPriceCard from "./QuotedPriceCard";
import SummaryStatTile from "./SummaryStatTile";
import EngineEstimateTile from "./EngineEstimateTile";
import EditableEstimateTile from "./EditableEstimateTile";
import PartsPriceTile from "./PartsPriceTile";
import PartsBreakdownPanel from "./PartsBreakdownPanel";
import PriceFormat from "../../../../utils/priceFormat";
import { ebayEngineSearchUrl } from "../../../../utils/ebaySearchString";
import useBookingMutations from "../../../../data/useBookingMutations";
import useSingleSetting from "../../../../data/useSingleSetting";


type OverrideKey = "enginePrice" | "gearboxPrice" | "ewwEngine" | "vlaEngine" | "catAvg";

const PARTS_LIST = [
  "Headlamp LH",
  "Headlamp RH",
  "Front Bumper",
  "Bonnet",
  "Door Front LH",
  "Door Front RH",
  "Door Rear LH",
  "Door Rear RH",
  "Tailgate",
  "Rear Bumper",
  "Rear Light LH",
  "Rear Light RH",
  "ECU",
  "ABS",
  "Air Con Pump",
];

const parseCurrencyInput = (value: string) => {
  const parsed = parseFloat(value);
  return !isNaN(parsed) && parsed >= 0 ? parsed : null;
};

const formatEstScrapPriceDisplay = (
  kerbWeightMin: number | null,
  kerbWeightMax: number | null,
  pricePerTonne: number
): string | null => {
  const scrapFromKg = (kg: number) => pricePerTonne * kg;

  const hasMin = kerbWeightMin != null;
  const hasMax = kerbWeightMax != null;

  if (hasMin && hasMax && kerbWeightMax !== kerbWeightMin) {
    return `${PriceFormat(scrapFromKg(kerbWeightMin))} - ${PriceFormat(scrapFromKg(kerbWeightMax))}`;
  }

  if (hasMin)  return PriceFormat(scrapFromKg(kerbWeightMin));
  if (hasMax) return PriceFormat(scrapFromKg(kerbWeightMax));
  
  return null;
};

export default function BookingPricingCard({ lead }: { lead: ILead }) {
  const { updatePrice } = useBookingMutations("BookingPricingCard");

  if (process.env.NODE_ENV !== "production") {
    const missing = [
      ["ContactModals", ContactModals],
      ["PricingHeader", PricingHeader],
      ["QuotedPriceCard", QuotedPriceCard],
      ["SummaryStatTile", SummaryStatTile],
      ["EngineEstimateTile", EngineEstimateTile],
      ["EditableEstimateTile", EditableEstimateTile],
      ["PartsPriceTile", PartsPriceTile],
      ["PartsBreakdownPanel", PartsBreakdownPanel],
      ["CurrencyPoundIcon", CurrencyPoundIcon],
      ["CalculatorIcon", CalculatorIcon],
      ["TruckIcon", TruckIcon],
      ["CubeIcon", CubeIcon],
      ["ScaleIcon", ScaleIcon],
    ]
      .filter(([, value]) => !value)
      .map(([name]) => name);

    if (missing.length > 0) {
      // Helps identify the exact import causing React's generic invalid element error.
      console.error("BookingPricingCard missing imports:", missing.join(", "));
    }
  }

  const SafeContactModals = ContactModals ?? (() => null);
  const SafePricingHeader = PricingHeader ?? (() => null);
  const SafeQuotedPriceCard = QuotedPriceCard ?? (() => null);
  const SafeSummaryStatTile = SummaryStatTile ?? (() => null);
  const SafeEngineEstimateTile = EngineEstimateTile ?? (() => null);
  const SafeEditableEstimateTile = EditableEstimateTile ?? (() => null);
  const SafePartsPriceTile = PartsPriceTile ?? (() => null);
  const SafePartsBreakdownPanel = PartsBreakdownPanel ?? (() => null);

  const SafeScaleIcon = ScaleIcon ?? (() => null);
  const SafeTruckIcon = TruckIcon ?? (() => null);
  const SafeCalculatorIcon = CalculatorIcon ?? (() => null);
  const SafeCurrencyPoundIcon = CurrencyPoundIcon ?? (() => null);

  const [editingPrice, setEditingPrice] = useState(false);
  const [adjustedPrice, setAdjustedPrice] = useState<number>(lead.price);
  const [priceBeforeEdit, setPriceBeforeEdit] = useState<number>(lead.price);
  const [priceSaved, setPriceSaved] = useState(false);
  const [priceChangeReason, setPriceChangeReason] = useState("");
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [notifySms, setNotifySms] = useState(true);

  const [overrides, setOverrides] = useState<Partial<Record<OverrideKey, number>>>({});
  const [editingTile, setEditingTile] = useState<OverrideKey | null>(null);
  const [editingTileValue, setEditingTileValue] = useState("");

  const [partsExpanded, setPartsExpanded] = useState(false);
  const [partPrices, setPartPrices] = useState<Record<string, number>>({});
  const [editingPart, setEditingPart] = useState<string | null>(null);
  const [editingPartValue, setEditingPartValue] = useState("");

  const [emailOpen, setEmailOpen] = useState(false);
  const [textOpen, setTextOpen] = useState(false);

  const vehicle = lead.vehicle;

  const ewwEngine =
    overrides.ewwEngine ??
    (vehicle?.enginesWorldwideEngineOffer ? parseFloat(vehicle.enginesWorldwideEngineOffer) : null);

  const vlaEngine =
    overrides.vlaEngine ?? (vehicle?.vlaEngineOffer ? parseFloat(vehicle.vlaEngineOffer) : null);

  const tradeValue = vehicle?.valuationTradePoor ?? null;
  const catAvg = overrides.catAvg ?? (lead.catalyticAveragePrice ?? null);
  const ebayEngine = vehicle?.ebayEnginePrice ?? null;
  const lastAuctionPrice = null as number | null;
  
  const { singleSetting: scrapPricePerTonneMultiple } = useSingleSetting("Scrap:PricePerTonneMultiplier");
  const pricePerTonne = parseFloat(scrapPricePerTonneMultiple.data?.data?.value ?? "1");
  const kerbWeightMin = vehicle?.kerbWeightMin ?? null;
  const kerbWeightMax = vehicle?.kerbWeightMax ?? null;
  const estScrapPriceDisplay = formatEstScrapPriceDisplay(kerbWeightMin, kerbWeightMax, pricePerTonne);
  const estEnginePrice = overrides.enginePrice ?? ewwEngine ?? ebayEngine ?? null;
  const estGearboxPrice = overrides.gearboxPrice ?? null;

  const partsTotal = Object.values(partPrices).reduce((sum, value) => sum + value, 0);
  const estPartsPrice = partsTotal > 0 ? partsTotal : (vlaEngine ?? 0) + (catAvg ?? 0);
  const corePartsValue = (estEnginePrice ?? 0) + (estGearboxPrice ?? 0) + estPartsPrice;

  const make = vehicle?.combinedMake ?? "";
  const model = vehicle?.combinedModel ?? "";

  const ebayEngineUrl = vehicle ? ebayEngineSearchUrl(vehicle) ?? "" : "";

  const ebayGearboxUrl = `https://www.ebay.co.uk/sch/i.html?_nkw=${encodeURIComponent(
    `${make} ${model} gearbox`.trim()
  )}&_sacat=0`;

  const startTileEdit = (key: OverrideKey, currentValue?: number | null) => {
    setEditingTile(key);
    setEditingTileValue(currentValue != null ? String(currentValue) : "");
  };

  const saveTileEdit = (key: OverrideKey) => {
    const value = parseCurrencyInput(editingTileValue);
    if (value != null) {
      setOverrides((prev) => ({ ...prev, [key]: value }));
    }
    cancelTileEdit();
  };

  const cancelTileEdit = () => {
    setEditingTile(null);
    setEditingTileValue("");
  };

  const startPartEdit = (part: string) => {
    setEditingPart(part);
    setEditingPartValue(partPrices[part] != null ? String(partPrices[part]) : "");
  };

  const savePartEdit = () => {
    if (!editingPart) return;

    const value = parseCurrencyInput(editingPartValue);
    if (value != null) {
      setPartPrices((prev) => ({ ...prev, [editingPart]: value }));
    }

    cancelPartEdit();
  };

  const cancelPartEdit = () => {
    setEditingPart(null);
    setEditingPartValue("");
  };

  const handleSavePrice = () => {
    setPriceSaved(true);
  };

  const handleStartPriceEditing = () => {
    setPriceBeforeEdit(adjustedPrice);
    setEditingPrice(true);
  };

  const handleCancelPriceEditing = () => {
    setAdjustedPrice(priceBeforeEdit);
    setEditingPrice(false);
    setPriceSaved(false);
    setPriceChangeReason("");
  };

  const handleUpdateAndNotify = () => {
    if (notifyEmail) setEmailOpen(true);
    if (notifySms && !notifyEmail) setTextOpen(true);

    setEditingPrice(false);
    setPriceSaved(false);
    setPriceChangeReason("");
  };

  const handleDoneEditing = () => {
    if (updatePrice.isLoading) return;

    updatePrice.mutate(
      {
        leadId: lead.id,
        price: adjustedPrice,
        reason: priceChangeReason.trim() || "No reason provided",
      },
      {
        onSuccess: () => {
          setEditingPrice(false);
          setPriceSaved(false);
          setPriceChangeReason("");
        },
      }
    );
  };

  const summaryTiles = [
    {
      label: "Scrap Price",
      value: null,
      displayValue: estScrapPriceDisplay,
      icon: <SafeScaleIcon className="h-3.5 w-3.5 text-gray-500" />,
    },
    {
      label: "Trade Value",
      value: tradeValue,
      icon: <SafeTruckIcon className="h-3.5 w-3.5 text-teal-500" />,
    },
    {
      label: "Last Auction",
      value: lastAuctionPrice,
      icon: <SafeCalculatorIcon className="h-3.5 w-3.5 text-amber-500" />,
    },
    {
      label: "Commission",
      value: lead.commission,
      icon: <SafeCurrencyPoundIcon className="h-3.5 w-3.5 text-gray-400" />,
      valueClassName: "text-gray-700",
    },
  ];

  return (
    <>
      <SafeContactModals
        lead={lead}
        emailOpen={emailOpen}
        textOpen={textOpen}
        setEmailOpen={setEmailOpen}
        setTextOpen={setTextOpen}
      />

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <SafePricingHeader quoteType={lead.quoteType} />

        <div className="space-y-4 p-4">
          <div className="flex items-center gap-6 border-b border-gray-100 pb-4">
            <SafeQuotedPriceCard
              value={adjustedPrice}
              applyVAT={lead.applyVAT}
              editing={editingPrice}
              priceSaved={priceSaved}
              priceChangeReason={priceChangeReason}
              notifyEmail={notifyEmail}
              notifySms={notifySms}
              onStartEditing={handleStartPriceEditing}
              onPriceChange={(value) => {
                setAdjustedPrice(value);
                setPriceSaved(false);
              }}
              onSavePrice={handleSavePrice}
              onCancelEditing={handleCancelPriceEditing}
              onReasonChange={setPriceChangeReason}
              onNotifyEmailChange={setNotifyEmail}
              onNotifySmsChange={setNotifySms}
              onUpdateAndNotify={handleUpdateAndNotify}
              onDoneEditing={handleDoneEditing}
            />

            <div className="grid flex-1 grid-cols-4 gap-3">
              {summaryTiles.map((tile) => (
                <SafeSummaryStatTile
                  key={tile.label}
                  label={tile.label}
                  value={tile.value}
                  displayValue={tile.displayValue}
                  icon={tile.icon}
                  valueClassName={tile.valueClassName}
                />
              ))}
            </div>
          </div>

          <div>
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
              Estimated Values
            </p>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
              <SafeEngineEstimateTile
                value={estEnginePrice}
                ebayUrl={ebayEngineUrl}
                hasOverride={overrides.enginePrice !== undefined}
                mainEditing={editingTile === "enginePrice"}
                mainInputValue={editingTileValue}
                onMainInputChange={setEditingTileValue}
                onMainStartEdit={() => startTileEdit("enginePrice", estEnginePrice)}
                onMainSave={() => saveTileEdit("enginePrice")}
                onMainCancel={cancelTileEdit}
                ewwValue={ewwEngine}
                ewwEditing={editingTile === "ewwEngine"}
                onEwwStartEdit={() => startTileEdit("ewwEngine", ewwEngine)}
                onEwwSave={() => saveTileEdit("ewwEngine")}
                vlaValue={vlaEngine}
                vlaEditing={editingTile === "vlaEngine"}
                onVlaStartEdit={() => startTileEdit("vlaEngine", vlaEngine)}
                onVlaSave={() => saveTileEdit("vlaEngine")}
                sourceInputValue={editingTileValue}
                onSourceInputChange={setEditingTileValue}
                onSourceCancel={cancelTileEdit}
              />

              <SafeEditableEstimateTile
                label="Gearbox Price"
                value={estGearboxPrice}
                icon={<CubeIcon className="h-3.5 w-3.5" />}
                iconClassName="text-indigo-500"
                hasOverride={overrides.gearboxPrice !== undefined}
                lookupUrl={ebayGearboxUrl}
                isEditing={editingTile === "gearboxPrice"}
                inputValue={editingTileValue}
                onInputChange={setEditingTileValue}
                onStartEdit={() => startTileEdit("gearboxPrice", estGearboxPrice)}
                onSave={() => saveTileEdit("gearboxPrice")}
                onCancel={cancelTileEdit}
              />

              <SafePartsPriceTile
                value={estPartsPrice}
                partsTotal={partsTotal}
                partsCount={Object.keys(partPrices).length}
                expanded={partsExpanded}
                onToggle={() => setPartsExpanded((prev) => !prev)}
              />

              <SafeEditableEstimateTile
                label="Cat Average"
                value={catAvg}
                icon={<CalculatorIcon className="h-3.5 w-3.5" />}
                iconClassName="text-amber-500"
                hasOverride={overrides.catAvg !== undefined}
                isEditing={editingTile === "catAvg"}
                inputValue={editingTileValue}
                onInputChange={setEditingTileValue}
                onStartEdit={() => startTileEdit("catAvg", catAvg)}
                onSave={() => saveTileEdit("catAvg")}
                onCancel={cancelTileEdit}
              />

              <div className="rounded-lg border border-green-200 bg-green-50 p-3">
                <div className="mb-1 flex items-center gap-1.5">
                  <SafeCurrencyPoundIcon className="h-3.5 w-3.5 text-green-600" />
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-green-700">
                    Core Parts Value
                  </span>
                </div>
                <p className="text-sm font-bold text-green-700">{PriceFormat(corePartsValue)}</p>
              </div>
            </div>

            <SafePartsBreakdownPanel
              expanded={partsExpanded}
              partPrices={partPrices}
              editingPart={editingPart}
              editingPartValue={editingPartValue}
              onPartInputChange={setEditingPartValue}
              onStartEdit={startPartEdit}
              onSave={savePartEdit}
              onCancel={cancelPartEdit}
              total={partsTotal}
              partsList={PARTS_LIST}
            />
          </div>
        </div>
      </div>
    </>
  );
}